import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { PageShell } from './layout';
import { submitContactMessage } from '../api/contact';
import styles from '../styles/Contatti.module.css';
import FaIcon from './FaIcon';

// Message type options
const MESSAGE_TYPES = [
  { value: 'bug_report', label: '🐛 Segnalazione bug', description: 'Qualcosa non funziona come dovrebbe' },
  { value: 'feature_request', label: '💡 Suggerimento', description: 'Un\'idea per migliorare i giochi' },
  { value: 'adulation', label: '💖 Complimento', description: 'Ci vuoi far felici?' },
  { value: 'collaboration', label: '🤝 Collaborazione', description: 'Vuoi collaborare con noi?' },
  { value: 'other', label: '💬 Altro', description: 'Qualsiasi altra cosa' },
];

// Basic email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const Contatti = () => {
  const { user, isSignedIn } = useUser();
  
  // Form state
  const [email, setEmail] = useState(() => {
    // Pre-fill with user's email if signed in
    if (isSignedIn && user?.primaryEmailAddress?.emailAddress) {
      return user.primaryEmailAddress.emailAddress;
    }
    // Otherwise try localStorage
    try {
      return localStorage.getItem('email') || '';
    } catch {
      return '';
    }
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | 'rate_limited'
  const [errorMessage, setErrorMessage] = useState('');
  
  // Form validation state
  const [emailError, setEmailError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [typeError, setTypeError] = useState('');
  
  const formRef = useRef(null);

  // Character count
  const charCount = message.length;
  const maxChars = 1000;
  const charCountClass = charCount > maxChars ? styles.charCountOver : 
                         charCount > maxChars * 0.9 ? styles.charCountWarning : '';

  // Validate email
  const validateEmail = (value) => {
    if (!value.trim()) {
      setEmailError('L\'email è obbligatoria');
      return false;
    }
    if (!EMAIL_REGEX.test(value)) {
      setEmailError('Inserisci un indirizzo email valido');
      return false;
    }
    if (value.length > 254) {
      setEmailError('L\'email è troppo lunga');
      return false;
    }
    setEmailError('');
    return true;
  };

  // Validate message
  const validateMessage = (value) => {
    if (!value.trim()) {
      setMessageError('Il messaggio è obbligatorio');
      return false;
    }
    if (value.trim().length < 10) {
      setMessageError('Il messaggio deve essere di almeno 10 caratteri');
      return false;
    }
    if (value.length > maxChars) {
      setMessageError(`Il messaggio non può superare i ${maxChars} caratteri`);
      return false;
    }
    setMessageError('');
    return true;
  };

  // Validate type
  const validateType = (value) => {
    if (!value) {
      setTypeError('Seleziona un tipo di messaggio');
      return false;
    }
    setTypeError('');
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset status
    setSubmitStatus(null);
    setErrorMessage('');
    
    // Validate all fields
    const isEmailValid = validateEmail(email);
    const isMessageValid = validateMessage(message);
    const isTypeValid = validateType(messageType);
    
    if (!isEmailValid || !isMessageValid || !isTypeValid) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const data = {
        email: email.trim().toLowerCase(),
        message: message.trim(),
        message_type: messageType,
        clerk_user_id: isSignedIn && user?.id ? user.id : null,
      };
      
      await submitContactMessage(data);
      
      setSubmitStatus('success');
      // Clear form on success
      setMessage('');
      setMessageType('');
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      
      if (error.response?.status === 429) {
        setSubmitStatus('rate_limited');
        setErrorMessage('Hai già inviato un messaggio di recente. Riprova tra qualche minuto.');
      } else if (error.response?.data?.detail) {
        setSubmitStatus('error');
        setErrorMessage(error.response.data.detail);
      } else {
        setSubmitStatus('error');
        setErrorMessage('Si è verificato un errore. Riprova più tardi.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        {/* Decorative envelope particles */}
        <div className={styles.particles}>
          {['✉️', '💌', '📧', '📬', '✨', '💫'].map((emoji, i) => (
            <span key={i} className={styles.particle}>{emoji}</span>
          ))}
        </div>
        
        <div className={styles.heroContent}>
          <div className={styles.heroEmoji}>📮</div>
          <h1 className={styles.heroTitle}>Contattaci</h1>
          <p className={styles.heroSubtitle}>
            Hai trovato un bug? Un'idea geniale? O semplicemente vuoi farci un saluto? Siamo tutt'orecchi! 👂
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className={styles.formSection}>
        <div className={styles.formContainer}>
          {submitStatus === 'success' ? (
            <div className={styles.successCard}>
              <div className={styles.successIcon}>🎉</div>
              <h2>Messaggio inviato!</h2>
              <p>
                Grazie per averci scritto! Ti risponderemo al più presto 
                all'indirizzo <strong>{email}</strong>.
              </p>
              <div className={styles.successActions}>
                <button 
                  onClick={() => setSubmitStatus(null)} 
                  className={styles.sendAnotherBtn}
                >
                  Invia un altro messaggio
                </button>
                <Link to="/" className={styles.backHomeBtn}>
                  Torna ai giochi
                </Link>
              </div>
            </div>
          ) : (
            <form ref={formRef} onSubmit={handleSubmit} className={styles.form}>
              {/* Message Type Selection */}
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Di cosa si tratta?</label>
                <div className={styles.typeGrid}>
                  {MESSAGE_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      className={`${styles.typeCard} ${messageType === type.value ? styles.typeCardSelected : ''}`}
                      onClick={() => {
                        setMessageType(type.value);
                        setTypeError('');
                      }}
                    >
                      <span className={styles.typeLabel}>{type.label}</span>
                      <span className={styles.typeDescription}>{type.description}</span>
                    </button>
                  ))}
                </div>
                {typeError && <span className={styles.fieldError}>{typeError}</span>}
              </div>

              {/* Email Field */}
              <div className={styles.fieldGroup}>
                <label htmlFor="email" className={styles.label}>
                  La tua email
                  <span className={styles.labelHint}>per poterti rispondere</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) validateEmail(e.target.value);
                  }}
                  onBlur={(e) => validateEmail(e.target.value)}
                  placeholder="mario@esempio.it"
                  className={`${styles.input} ${emailError ? styles.inputError : ''}`}
                  disabled={isSubmitting}
                  autoComplete="email"
                />
                {emailError && <span className={styles.fieldError}>{emailError}</span>}
              </div>

              {/* Message Field */}
              <div className={styles.fieldGroup}>
                <label htmlFor="message" className={styles.label}>
                  Il tuo messaggio
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    if (messageError) validateMessage(e.target.value);
                  }}
                  onBlur={(e) => validateMessage(e.target.value)}
                  placeholder="Scrivi qui il tuo messaggio..."
                  className={`${styles.textarea} ${messageError ? styles.inputError : ''}`}
                  rows={6}
                  disabled={isSubmitting}
                />
                <div className={styles.textareaFooter}>
                  {messageError && <span className={styles.fieldError}>{messageError}</span>}
                  <span className={`${styles.charCount} ${charCountClass}`}>
                    {charCount}/{maxChars}
                  </span>
                </div>
              </div>

              {/* Error Message */}
              {(submitStatus === 'error' || submitStatus === 'rate_limited') && (
                <div className={styles.errorBanner}>
                  <span className={styles.errorIcon}>⚠️</span>
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className={styles.spinner}></span>
                    Invio in corso...
                  </>
                ) : (
                  <>
                    <FaIcon className="fas fa-paper-plane"  />
                    Invia messaggio
                  </>
                )}
              </button>

              <p className={styles.privacyNote}>
                <FaIcon className="fas fa-lock"  />
                Le tue informazioni saranno trattate secondo la nostra{' '}
                <Link to="/privacy">Privacy Policy</Link>.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Info Cards Section */}
      <section className={styles.infoSection}>
        <div className={styles.infoGrid}>
          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>⏱️</div>
            <h3>Rispondiamo velocemente</h3>
            <p>Di solito entro 24-48 ore. Se è urgente, faccelo sapere nel messaggio!</p>
          </div>
          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>🤖</div>
            <h3>Leggiamo tutto</h3>
            <p>Non usiamo risposte automatiche. Mario (il nostro robot) smista, ma rispondono gli umani.</p>
          </div>
          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>💡</div>
            <h3>Le idee contano</h3>
            <p>Molte funzionalità sono nate dai suggerimenti degli utenti. La tua potrebbe essere la prossima!</p>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Contatti;
